import * as three from "three";
import {
  getLollipopBaseMesh,
  getSelectionMesh,
  getPetalsMesh,
  drawPetal,
  drawBase,
  drawSelection,
  updateVisPetal,
  updateVisBase,
  updatePetalColor,
} from "./pathogenicityBlueprint";
import pathogenicityTicks from "./pathogenicityTicks";
import {
  pathogenic,
  benign,
  vus,
  impactColorIndex,
  impactLookup,
} from "../../constants/pathogenicity";
import Variant from "../../../../models/Variant";
import Lollipop from "./models/Lollipop";
import LollipopPetal from "./models/LollipopPetal";
import PathogenicityDetails from "./models/PathogenicityDetails";
import { getSelectionColorMap } from "../../constants/colors";
import getFilters from "./getFilters";
import {
  PATHOGENICITY_CL,
  IMPACT_NULL_VARS_CL,
  IMPACT_OTHER_CL,
} from "../../constants/options";
import ColorScale from "../../models/ColorScale";

const colorBy = "color by";
const colorByPathogenicity = "Pathogenicity";
const colorByImpact = "Coding Impact";
const colorByKeys = { colorByPathogenicity, colorByImpact, colorBy };

function getDefaultState(track) {
  const options = track.getTrackOptions() || {};
  const selectedColorBy = options.colorBy || colorByImpact;
  return {
    options,
    variantDict: {},
    lollipopPetals: [],
    lollipopBases: [],
    selections: [],
    pathColors: [],
    impactColors: [],
    colorBy: selectedColorBy,
    variantToPetalMap: new Map(),
    impactToDetailMap: new Map(),
    dbToDetailMap: new Map(),
    config: null,
    filters: getFilters(colorByKeys, selectedColorBy),
  };
}

function pathogenicityTrackController(track) {
  let state = getDefaultState(track);
  const lollipopBaseMesh = getLollipopBaseMesh();
  const selectionMesh = getSelectionMesh();
  const petalsMesh = getPetalsMesh();

  function onReset() {
    state = getDefaultState(track);
    setColorScale(state.colorBy, true);
  }

  function onInit() {
    track.setFilters([
      ...state.filters.dbFilters,
      ...state.filters.impactFilters,
      ...state.filters.colorByFilters,
    ]);
    track.setAxisProps({ ticks: pathogenicityTicks });
    setColorScale(state.colorBy, true);
  }

  function onRangeChange({ chrSegments }) {
    track.loadPosition(chrSegments);
  }

  function getLollipopId(v) {
    return `${v.variant.region.g0}-${v.pathoIndex}`;
  }

  function onDataReceived({ res, chr }) {
    if (!res || !res.map) return;

    const nonClictingItems = res.filter((x) => !x.is_conflicting);
    const conflictingItems = res
      .filter((x) => x.is_conflicting)
      .map((x) => {
        const conflict = true;
        return [
          x.submissions_b
            ? { ...x, acmg_class: benign, count: x.submissions_b, conflict }
            : null,
          x.submissions_p
            ? { ...x, acmg_class: pathogenic, count: x.submissions_p, conflict }
            : null,
          x.submissions_vus
            ? { ...x, acmg_class: vus, count: x.submissions_vus, conflict }
            : null,
        ].filter(Boolean);
      })
      .reduce((a, i) => a.concat(i), []);

    const items = nonClictingItems.concat(conflictingItems).map((x) => {
      const existing = track.variantRegistry.get(x.variant_id);
      const variant = existing || new Variant(x, chr);
      if (!existing) track.variantRegistry.set(variant.id, variant);
      return new PathogenicityDetails(x, variant);
    });

    const lollipopMap = items.reduce((a, i) => {
      const id = getLollipopId(i);
      const existing = a.get(id) || new Lollipop();
      existing.addVariant(i);
      return a.set(id, existing);
    }, new Map());

    const petalMap = items.reduce((a, i) => {
      const petal = a.get(i.id) || new LollipopPetal();
      const lollipop = lollipopMap.get(getLollipopId(i));
      petal.addVariant(i);
      petal.addToLollipop(lollipop);

      return a.set(i.id, petal);
    }, new Map());

    const lollipopArr = Array.from(lollipopMap.values());
    const petalArr = Array.from(petalMap.values());

    state.lollipopPetals = state.lollipopPetals.concat(petalArr);
    state.lollipopBases = state.lollipopBases.concat(lollipopArr);

    items.forEach((x) => {
      const { codingImpactIndex: ciIndex, sourceIndex: sIndex } = x;

      const existingItoD = state.impactToDetailMap.get(ciIndex) || new Set();
      const existingDtoD = state.dbToDetailMap.get(sIndex) || new Set();

      existingDtoD.add(x.variant.id);
      existingItoD.add(x.variant.id);

      state.dbToDetailMap.set(sIndex, existingDtoD);
      state.impactToDetailMap.set(ciIndex, existingItoD);
    });

    petalArr.forEach((x) => {
      const { variant } = x.pathogenicityDetails[0];
      const existingVtoP = state.variantToPetalMap.get(variant?.id) || [];

      existingVtoP.push(x);
      state.variantToPetalMap.set(variant.id, existingVtoP);
    });

    lollipopBaseMesh.setItems(state.lollipopBases);
    petalsMesh.setItems(state.lollipopPetals);

    updateSelection();
    drawBase(lollipopArr, lollipopBaseMesh);
    drawPetal(petalArr, petalsMesh, state.colorBy, colorByKeys);

    calcFilters();
    applyFilters();
    render();
  }

  function calcFilters() {
    state.filters.impactFilters.forEach((x) => {
      const variantSet = state.impactToDetailMap.get(x.key) || new Set();
      x.totalCount = variantSet.size;
    });
    state.filters.dbFilters.forEach((x) => {
      const variantSet = state.dbToDetailMap.get(x.key) || new Set();
      x.totalCount = variantSet.size;
    });

    track.setFilters([
      ...state.filters.impactFilters,
      ...state.filters.dbFilters,
      ...state.filters.colorByFilters,
    ]);
  }

  function updateSelection() {
    const activeFilters = getActiveFilters();
    const { selectedVariantId } = track.getSharedState();

    selectionMesh.reset();

    if (!selectedVariantId) return;

    state.selections = state.variantToPetalMap.get(selectedVariantId) || [];

    selectionMesh.setItems(state.selections);
    drawSelection(state.selections, selectionMesh);
    updateVisPetal(state.selections, selectionMesh, activeFilters, colorByKeys);
    render();
  }

  function render() {
    track.requestRender();
  }

  function onSharedStateChange() {
    updateSelection();
  }

  function onPreferencesChanged() {
    const config = track.getPreferences();
    state.config = config;
    const { isDark } = config;
    const pathoScale = config[PATHOGENICITY_CL];
    const impactNullScale = config[IMPACT_NULL_VARS_CL];
    const impactOtherScale = config[IMPACT_OTHER_CL];
    const getGray = () =>
      isDark
        ? new three.Vector4(0.2, 0.2, 0.3, 1)
        : new three.Vector4(0.8, 0.8, 0.85, 1);

    const vec = getSelectionColorMap(isDark);
    const theme = isDark ? 2 : 1;
    const a = isDark ? 0.7 : 0.8;

    const cScalePatho = new ColorScale(pathoScale, 5, 11)
      .setTheme(theme)
      .setOpacity(a);

    const cScaleNull = new ColorScale(impactNullScale, 6)
      .setTheme(theme)
      .setOpacity(a);

    const cScaleOther = new ColorScale(impactOtherScale, 4)
      .setTheme(theme)
      .setOpacity(a);

    const { vectors: pathVecs, hexes: pathHexes } = cScalePatho;
    const { vectors: nullVecs, hexes: nullHex } = cScaleNull;
    const { vectors: otherVecs, hexes: otherHex } = cScaleOther;
    const impactHexes = ["#000000"].concat(otherHex.concat(nullHex));
    const impactVecs = [getGray()].concat(otherVecs.concat(nullVecs));
    const impactBaseVecs = Array(11).fill(0).map(getGray);

    const impactCol = state.colorBy === colorByImpact;
    const vectors = impactCol ? impactVecs : pathVecs;
    const baseVecs = impactCol ? impactBaseVecs : pathVecs;
    const isCodingColor = state.colorBy === colorByImpact;

    state.filters.impactFilters.forEach((x) => {
      const rgb = impactHexes[impactColorIndex[impactLookup[x.key]]];
      x.color = isCodingColor ? rgb : null;
    });

    track.setFilters([
      ...state.filters.impactFilters,
      ...state.filters.dbFilters,
      ...state.filters.colorByFilters,
    ]);

    state.impactColors = impactHexes;
    state.pathColors = pathHexes;
    selectionMesh.uniforms.setColorMap([vec]);

    petalsMesh.uniforms.setColorMap(vectors);
    lollipopBaseMesh.uniforms.setColorMap(baseVecs);

    petalsMesh.material.blending = isDark
      ? three.CustomBlending
      : three.NormalBlending;
    petalsMesh.material.blendEquation = three.AddEquation;
    petalsMesh.material.blendSrc = three.SrcAlphaFactor;
    petalsMesh.material.blendDst = three.OneMinusSrcAlphaFactor;

    render();
  }

  function setColorScale(filterColorBy, force) {
    if (state.colorBy !== filterColorBy || force) {
      state.colorBy = filterColorBy;
      track.setTrackOptions({ ...track.options, colorBy: filterColorBy });
      const active = getActiveFilters();
      const { lollipopPetals } = state;
      setColorScale(active.colorBy);
      onPreferencesChanged();
      updatePetalColor(lollipopPetals, petalsMesh, state.colorBy, colorByKeys);
    }
  }

  function onFiltersChange(updatedFilters) {
    const pathDbCat = state.filters.dbFilters[0].category;
    const isDb = (x) => x.category === pathDbCat;
    const isImpact = (x) => x.category !== pathDbCat && x.key !== colorBy;
    const isColorBy = (x) => x.key === colorBy;

    state.filters.dbFilters = updatedFilters.filter(isDb);
    state.filters.impactFilters = updatedFilters.filter(isImpact);
    state.filters.colorBy = updatedFilters.filter(isColorBy);

    track.setFilters([...updatedFilters]);

    setTimeout(() => {
      applyFilters();
      render();
    }, 50);
  }

  function getActiveFilters() {
    const activeImpact = state.filters.impactFilters
      .filter((x) => x.value)
      .map((x) => x.key);
    const activeDb = state.filters.dbFilters
      .filter((x) => x.value)
      .map((x) => x.key);

    return {
      dbFilters: new Set(activeDb),
      impactFilters: new Set(activeImpact),
      colorBy: state.filters.colorByFilters?.[0]?.value,
    };
  }

  function applyFilters() {
    const active = getActiveFilters();

    setColorScale(active.colorBy);
    updateVisPetal(state.lollipopPetals, petalsMesh, active, colorByKeys);
    updateVisPetal(state.selections, selectionMesh, active, colorByKeys);
    updateVisBase(state.lollipopBases, lollipopBaseMesh, active, colorByKeys);
    render();
  }

  function getState() {
    return state;
  }

  return {
    onReset,
    onPreferencesChanged,
    onInit,
    onRangeChange,
    onSharedStateChange,
    onFiltersChange,
    onDataReceived,
    getState,
    meshes: [lollipopBaseMesh, petalsMesh, selectionMesh],
  };
}

export default pathogenicityTrackController;
